import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, HardHat, MapPin, RefreshCw, Search, Settings2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { PageContainer } from '../../../common/components/PageContainer';
import { EmptyState } from '../../../components/design-system/admin/EmptyState';
import { KpiCard } from '../../../components/design-system/admin/KpiCard';
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { StatusBadge } from '../../../components/design-system/admin/StatusBadge';
import { TablePagination } from '../../../components/design-system/admin/TablePagination';
import { BarList } from '../../../components/design-system/analytics/BarList';
import {
    TRADE_STATUSES, TradeMapPoint, TradePlace, TradeProspect, TradeProspectPage, TradeProspectQuery, TradeSettingsView, useTradeApi,
} from '../../../common/hooks/use-trade-api.hook';
import { ProspectSheet } from './components/ProspectSheet';
import { TradeLeadsSection } from './components/TradeLeadsSection';
import { TradeMap } from './components/TradeMap';
import { TradeOriginInput } from './components/TradeOriginInput';
import { TradeSettingsSheet } from './components/TradeSettingsSheet';
import { STATUS_TONE, activityLabel, formatKm, headcountShort, sinceYear } from './trade-format';

type SortKey = NonNullable<TradeProspectQuery['sort']>;
const SORTS: SortKey[] = ['score', 'distance', 'size', 'createdOn', 'name'];
const PAGE_SIZE = 25;

/**
 * Trade prospects (feat/room-to-revenue, workstream F1).
 *
 * Professionals who buy what the vendor sells — tilers, fitters, architects,
 * interior designers, builders — within their delivery radius, from the public
 * company register, ranked, each with an introduction one click away. Works on
 * day one, before the vendor's website has had a single visitor. Below them,
 * the professionals already hiding among the vendor's leads.
 */
export const TradePage = () => {
    const { t, i18n } = useTranslation();
    const api = useTradeApi();
    const locale = i18n.language || 'en';

    const [view, setView] = useState<TradeSettingsView | null>(null);
    const [data, setData] = useState<TradeProspectPage | null>(null);
    const [points, setPoints] = useState<TradeMapPoint[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [selected, setSelected] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    const [search, setSearch] = useState('');
    const [term, setTerm] = useState('');
    const [status, setStatus] = useState<NonNullable<TradeProspectQuery['status']>>('open');
    const [activity, setActivity] = useState<string>('all');
    const [sort, setSort] = useState<SortKey>('score');
    const [page, setPage] = useState(0);

    const [address, setAddress] = useState('');
    // The suggestion picked for `address`: saved as a point, so the API does not geocode it again.
    const [place, setPlace] = useState<TradePlace | null>(null);
    const [savingOrigin, setSavingOrigin] = useState(false);

    // Typing settles before the list reloads.
    useEffect(() => {
        const id = setTimeout(() => { setTerm(search.trim()); setPage(0); }, 300);
        return () => clearTimeout(id);
    }, [search]);

    const query: TradeProspectQuery = useMemo(() => ({
        status,
        activity: activity === 'all' ? undefined : activity,
        q: term || undefined,
        sort,
        page: page + 1,
        limit: PAGE_SIZE,
    }), [status, activity, term, sort, page]);

    const loadSettings = useCallback(async () => {
        const v = await api.getSettings();
        setView(v);
        return v;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadSettings().catch(() => toast.error(t('TRADE.ERRORS.LOAD_SETTINGS', 'Could not load your prospecting settings.')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([api.list(query), api.mapPoints({ ...query, page: undefined, limit: undefined })])
            .then(([list, pts]) => { if (!cancelled) { setData(list); setPoints(pts); } })
            .catch(() => { if (!cancelled) toast.error(t('TRADE.ERRORS.LOAD', 'Could not load the prospects.')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, reloadKey]);

    const refresh = async () => {
        setRefreshing(true);
        try {
            const res = await api.refresh();
            toast.success(t('TRADE.REFRESH.DONE', '{{found}} professionals found, {{created}} new.', { found: res.found, created: res.created }));
            await loadSettings();
            setPage(0);
            setReloadKey((k) => k + 1);
        } catch (e: any) {
            const code = e?.body?.code;
            if (code === 'NO_ORIGIN') setSettingsOpen(true);
            toast.error(code === 'REFRESH_RUNNING'
                ? t('TRADE.REFRESH.RUNNING', 'A refresh is already running.')
                : code === 'NO_ORIGIN'
                    ? t('TRADE.REFRESH.NO_ORIGIN', 'Tell us where you are first.')
                    : t('TRADE.REFRESH.FAILED', 'The company register did not answer. Try again in a minute.'));
        } finally {
            setRefreshing(false);
        }
    };

    const saveOrigin = async () => {
        if (!place && !address.trim()) return;
        setSavingOrigin(true);
        try {
            setView(await api.updateSettings(place
                ? { origin: { lat: place.lat, lng: place.lng, label: place.label } }
                : { address: address.trim() }));
            await refresh();
        } catch (e: any) {
            toast.error(e?.body?.code === 'ADDRESS_NOT_FOUND'
                ? t('TRADE.SETTINGS.ADDRESS_NOT_FOUND', 'That address could not be found on the map. Try a postcode and town.')
                : t('TRADE.ERRORS.SAVE', 'The change was not saved. Try again.'));
        } finally {
            setSavingOrigin(false);
        }
    };

    const exportCsv = async () => {
        try {
            const buffer = await api.exportCsv(query);
            const url = URL.createObjectURL(new Blob([buffer], { type: 'text/csv;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `prospects-pro-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            toast.error(t('TRADE.ERRORS.EXPORT', 'The file could not be prepared. Try again.'));
        }
    };

    const onChanged = (p: TradeProspect) => {
        setData((d) => (d ? { ...d, items: d.items.map((i) => (i._id === p._id ? { ...i, ...p } : i)) } : d));
        setPoints((list) => list.map((i) => (i._id === p._id ? { ...i, status: p.status } : i)));
    };

    const settings = view?.settings;
    const origin = settings?.origin;
    const counts = data?.counts;
    const last = settings?.lastRefresh;

    const heading = (
        <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
                <h1 className="font-heading text-2xl font-medium tracking-tight">{t('TRADE.TITLE', 'Trade prospects')}</h1>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    {t('TRADE.SUBTITLE', 'Tilers, fitters, architects, interior designers and builders near you — from the public company register, ranked, with an introduction ready. One contractor buys for a dozen projects a year.')}
                </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={() => setSettingsOpen(true)} disabled={!view} data-testid="trade-open-settings">
                    <Settings2 data-icon="inline-start" />
                    {t('TRADE.ACTIONS.SETTINGS', 'Settings')}
                </Button>
                {!!counts?.total && (
                    <Button variant="outline" onClick={exportCsv}>
                        <Download data-icon="inline-start" />
                        {t('TRADE.ACTIONS.EXPORT', 'Export CSV')}
                    </Button>
                )}
                {origin && (
                    <Button onClick={refresh} disabled={refreshing} data-testid="trade-refresh">
                        {refreshing ? <Spinner data-icon="inline-start" /> : <RefreshCw data-icon="inline-start" />}
                        {refreshing ? t('TRADE.ACTIONS.REFRESHING', 'Reading the register…') : t('TRADE.ACTIONS.REFRESH', 'Refresh now')}
                    </Button>
                )}
            </div>
        </div>
    );

    let body: ReactNode;
    if (!view) {
        body = <Skeleton className="h-64 w-full" />;
    } else if (!origin) {
        body = (
            <Card>
                <CardContent className="flex flex-col gap-4">
                    <div>
                        <h2 className="text-base font-medium">{t('TRADE.ORIGIN.TITLE', 'Where do you sell from?')}</h2>
                        <p className="mt-1 text-sm text-muted-foreground">{t('TRADE.ORIGIN.BODY', 'Your showroom or warehouse. We look for professionals around it, within your delivery distance.')}</p>
                    </div>
                    <div className="flex max-w-xl flex-col gap-2 sm:flex-row">
                        <TradeOriginInput
                            value={address}
                            onValueChange={(text) => {
                                setAddress(text);
                                setPlace(null);
                            }}
                            onPick={setPlace}
                            onSubmit={saveOrigin}
                            disabled={savingOrigin}
                        />
                        <Button onClick={saveOrigin} disabled={savingOrigin || !address.trim()}>
                            {savingOrigin ? <Spinner data-icon="inline-start" /> : <MapPin data-icon="inline-start" />}
                            {t('TRADE.ORIGIN.FIND', 'Find professionals')}
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('TRADE.SETTINGS.COUNTRY', 'France for now: professionals come from the public company register (SIRENE). Other countries’ registries will follow.')}</p>
                </CardContent>
            </Card>
        );
    } else if (!loading && !counts?.total) {
        body = (
            <Card>
                <EmptyState
                    icon={<HardHat />}
                    title={t('TRADE.EMPTY.TITLE', 'Find the professionals around {{place}}', { place: origin.label })}
                    description={t('TRADE.EMPTY.BODY', 'We read the public company register for the trades you chose within {{km}} km. It takes about ten seconds.', { km: settings.radiusKm })}
                    action={
                        <Button onClick={refresh} disabled={refreshing}>
                            {refreshing ? <Spinner data-icon="inline-start" /> : <Search data-icon="inline-start" />}
                            {t('TRADE.EMPTY.ACTION', 'Find them now')}
                        </Button>
                    }
                />
            </Card>
        );
    } else {
        const activityItems = (counts?.byActivity ?? []).map((a) => ({
            key: a.code,
            label: activityLabel(a.code, t, a.label),
            value: a.count,
            highlight: activity === a.code,
            onClick: () => { setActivity(activity === a.code ? 'all' : a.code); setPage(0); },
        }));
        body = (
            <>
                <p className="-mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground" data-testid="trade-origin-line">
                    <MapPin className="size-4" />
                    <span>{t('TRADE.ORIGIN.WITHIN', 'Within {{km}} km of {{place}}', { km: settings.radiusKm, place: origin.label })}</span>
                    {settings.lastRefreshAt && (
                        <>
                            <span aria-hidden>·</span>
                            <span>{t('TRADE.ORIGIN.CHECKED', 'register read')} <RelativeTime value={settings.lastRefreshAt} /></span>
                        </>
                    )}
                    {!!last?.available && (
                        <>
                            <span aria-hidden>·</span>
                            <span>{t('TRADE.ORIGIN.AVAILABLE', '{{available}} businesses in these trades in your area; the {{shown}} best placed are listed', {
                                available: new Intl.NumberFormat(locale).format(last.available), shown: new Intl.NumberFormat(locale).format(counts?.total ?? 0),
                            })}</span>
                        </>
                    )}
                </p>

                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <KpiCard label={t('TRADE.KPI.PROSPECTS', 'Professionals')} value={new Intl.NumberFormat(locale).format((counts?.total ?? 0) - (counts?.byStatus.ignored ?? 0))} loading={!counts} />
                    <KpiCard label={t('TRADE.KPI.CONTACTED', 'Contacted')} value={counts?.byStatus.contacted ?? 0} loading={!counts} />
                    <KpiCard label={t('TRADE.KPI.MEETINGS', 'Meetings')} value={counts?.byStatus.meeting ?? 0} loading={!counts} />
                    <KpiCard label={t('TRADE.KPI.CUSTOMERS', 'Trade customers')} value={counts?.byStatus.customer ?? 0} loading={!counts} />
                </div>

                <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                    <SectionCard title={t('TRADE.BY_ACTIVITY', 'By trade')} description={t('TRADE.BY_ACTIVITY_HINT', 'Click a trade to list only them.')}>
                        <BarList items={activityItems} />
                    </SectionCard>
                    <TradeMap
                        origin={origin}
                        radiusKm={settings.radiusKm}
                        points={points}
                        selectedId={selected}
                        onSelect={setSelected}
                        originLabel={t('TRADE.MAP.YOU', 'You')}
                    />
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <InputGroup className="w-full sm:w-72">
                        <InputGroupAddon><Search /></InputGroupAddon>
                        <InputGroupInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('TRADE.FILTERS.SEARCH', 'Search a name or a town')} />
                    </InputGroup>
                    <Select value={status} onValueChange={(v) => { setStatus(v as any); setPage(0); }}>
                        <SelectTrigger className="w-44" aria-label={t('TRADE.FILTERS.STATUS', 'Status')}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="open">{t('TRADE.FILTERS.OPEN', 'All but ignored')}</SelectItem>
                            {TRADE_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`TRADE.STATUS.${s.toUpperCase()}`, s)}</SelectItem>)}
                            <SelectItem value="all">{t('TRADE.FILTERS.ALL', 'All')}</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={activity} onValueChange={(v) => { setActivity(v); setPage(0); }}>
                        <SelectTrigger className="w-60" aria-label={t('TRADE.FILTERS.ACTIVITY', 'Trade')}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('TRADE.FILTERS.ALL_ACTIVITIES', 'All trades')}</SelectItem>
                            {(counts?.byActivity ?? []).map((a) => <SelectItem key={a.code} value={a.code}>{activityLabel(a.code, t, a.label)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={sort} onValueChange={(v) => { setSort(v as SortKey); setPage(0); }}>
                        <SelectTrigger className="w-48" aria-label={t('TRADE.FILTERS.SORT', 'Sort')}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {SORTS.map((s) => <SelectItem key={s} value={s}>{t(`TRADE.SORT.${s.toUpperCase()}`, s)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>

                <Card className="gap-0 py-0">
                    <CardContent className="px-0">
                        {loading && !data ? <Skeleton className="m-4 h-64" /> : (
                            <Table data-testid="trade-prospects-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="ps-4">{t('TRADE.TABLE.NAME', 'Business')}</TableHead>
                                        <TableHead>{t('TRADE.TABLE.ACTIVITY', 'Trade')}</TableHead>
                                        <TableHead className="text-end">{t('TRADE.TABLE.DISTANCE', 'Distance')}</TableHead>
                                        <TableHead className="text-end">{t('TRADE.TABLE.SIZE', 'Employees')}</TableHead>
                                        <TableHead className="text-end">{t('TRADE.TABLE.SINCE', 'Since')}</TableHead>
                                        <TableHead>{t('TRADE.TABLE.STATUS', 'Status')}</TableHead>
                                        <TableHead className="pe-4 text-end">{t('TRADE.TABLE.SCORE', 'Score')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(data?.items ?? []).map((p) => (
                                        <TableRow
                                            key={p._id}
                                            className="cursor-pointer"
                                            data-state={p._id === selected ? 'selected' : undefined}
                                            onClick={() => setSelected(p._id)}
                                            data-testid="trade-prospect-row"
                                        >
                                            <TableCell className="ps-4">
                                                <span className="flex max-w-72 min-w-0 flex-col">
                                                    <span className="truncate font-medium" title={p.name}>{p.tradeName || p.name}</span>
                                                    <span className="truncate text-xs text-muted-foreground">{[p.city, p.postcode].filter(Boolean).join(' · ')}</span>
                                                </span>
                                            </TableCell>
                                            <TableCell className="max-w-56 truncate text-sm">{activityLabel(p.activity.code, t, p.activity.label)}</TableCell>
                                            <TableCell className="text-end tabular-nums">{formatKm(p.distanceKm, locale)}</TableCell>
                                            <TableCell className="text-end tabular-nums">{p.soleTrader && headcountShort(p.headcountBand) === '—' ? t('TRADE.TABLE.SOLE', 'Sole trader') : headcountShort(p.headcountBand)}</TableCell>
                                            <TableCell className="text-end tabular-nums">{sinceYear(p.createdOn)}</TableCell>
                                            <TableCell><StatusBadge tone={STATUS_TONE[p.status]}>{t(`TRADE.STATUS.${p.status.toUpperCase()}`, p.status)}</StatusBadge></TableCell>
                                            <TableCell className="pe-4 text-end tabular-nums">{p.score}</TableCell>
                                        </TableRow>
                                    ))}
                                    {!loading && !data?.items.length && (
                                        <TableRow>
                                            <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                                                {t('TRADE.TABLE.NO_MATCH', 'No professional matches these filters.')}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                        {!!data?.total && (
                            <TablePagination page={page} pageSize={PAGE_SIZE} total={data.total} onPageChange={setPage} className="border-t" />
                        )}
                    </CardContent>
                </Card>
            </>
        );
    }

    return (
        <PageContainer breadcrumbs={[{ label: t('R2R.WORKSPACE.SALES') }, { label: t('R2R.NAV.TRADE') }]}>
            <div className="flex flex-col gap-6" data-testid="trade-page">
                {heading}
                {body}
                <TradeLeadsSection />
            </div>

            <ProspectSheet prospectId={selected} onClose={() => setSelected(null)} onChanged={onChanged} />
            <TradeSettingsSheet
                open={settingsOpen}
                view={view}
                onClose={() => setSettingsOpen(false)}
                onSaved={(next, refreshAfter) => {
                    setView(next);
                    setSettingsOpen(false);
                    setReloadKey((k) => k + 1);
                    if (refreshAfter) void refresh();
                    else toast.success(t('TRADE.SETTINGS.SAVED', 'Settings saved.'));
                }}
            />
        </PageContainer>
    );
};

export default TradePage;
